import { DepartmentResource } from '../../../entities/Departments/Departments'
import {
    DocumentTypeResource,
    DocumentViewModel,
    Role,
    UserDocumentViewModel,
    UserResource,
} from '../../../models'
import { toBase64, toUUID } from '../../helpers/transformers'
import { UUID } from '../../types'
import {
    FileFormData,
    FileV3Request,
    FileV3Response,
    FileViewModel,
} from './File'

/**
 * Transform FileFormData to V3 request body for POST/PATCH
 */
export const transformFileFormDataToV3Request = (data: FileFormData): FileV3Request => {
    const {
        name,
        ext,
        base64,
        documentType,
        roles,
        departments,
        file,
        userId,
        visibleToEmployee,
        hr,
    } = data

    const fileName = ext ? `${name}.${ext}` : name

    // Build viewers array for user-specific documents
    let viewers: UUID[] | undefined
    if (userId || hr) {
        viewers = [
            ...(hr || []),
            ...(visibleToEmployee && userId ? [userId] : []),
        ].filter(Boolean).map(toUUID)
    }

    return {
        ...(fileName && { fileName }),
        ...(name && { displayName: name }),
        ...(base64 && { content: toBase64(base64) }),
        ...(file?.type && { mimeType: file.type }),
        ...(documentType && { documentType: toUUID(documentType) }),
        ...(roles && roles.length > 0 && { roleAccess: roles.map(toUUID) }),
        ...(departments && departments.length > 0 && {
            departmentAccess: departments.map(d => toUUID(d.id)),
        }),
        ...(userId && { linkedUser: toUUID(userId) }),
        ...({ viewers }),
    }
}

/**
 * Transform V3 response to FileViewModel
 */
export const transformFileV3ResponseToFileViewModel = (response: FileV3Response): FileViewModel => {
    const downloadUrl = response.content
        ? `data:${response.mimeType};base64,${response.content}`
        : ''

    return {
        id: response.id,
        displayName: response.displayName,
        fileName: response.fileName,
        mimeType: response.mimeType,
        content: response.content,
        createdAt: new Date(response.createdAt),
        isDataUrl: true,
        downloadUrl,
        documentType: response.documentType
            ? {
                id: response.documentType.id,
                name: response.documentType.name,
                slug: response.documentType.slug,
            }
            : undefined,
        owner: response.owner
            ? {
                type: 'users',
                id: response.owner.id,
            } as UserResource
            : undefined,
        roleAccess: response.roleAccess?.map(role => ({
            type: 'roles' as const,
            id: role.id,
            name: role.name,
            slug: role.slug,
            public: false,
            assignable: false,
            priority: 0,
            permissions: [],
        })),
        departmentAccess: response.departmentAccess?.map(dept => ({
            type: 'departments' as const,
            id: dept.id,
            name: dept.name,
            group: dept.group,
        })),
        userCanView: response.userCanView,
        viewers: response.viewers?.map(v => v.id),
    }
}

/**
 * Transform V3 response to DocumentViewModel (for PZ documents list)
 */
export const transformFileV3ResponseToDocumentViewModel = (response: FileV3Response): DocumentViewModel => {
    const downloadUrl = response.content
        ? `data:${response.mimeType};base64,${response.content}`
        : ''

    return {
        id: response.id,
        displayName: response.displayName,
        documentType: response.documentType as DocumentTypeResource,
        userTypes: (response.roleAccess || []).map(role => ({
            type: 'roles' as const,
            id: role.id,
            name: role.name,
            slug: role.slug,
            public: false,
            assignable: false,
            priority: 0,
            permissions: [],
        })),
        departments: (response.departmentAccess || []).map(dept => ({
            type: 'departments' as const,
            id: dept.id,
            name: dept.name,
            group: dept.group,
        })),
        owner: response.owner as UserResource,
        downloadUrl,
        fileName: response.fileName,
        isDataUrl: true,
        createdAt: new Date(response.createdAt),
    }
}

/**
 * Transform V3 response to UserDocumentViewModel (for user documents in management)
 */
export const transformFileV3ResponseToUserDocumentViewModel = (
    response: FileV3Response,
    userId: string,
    userDepartments: DepartmentResource[],
    userRoles: Role[],
): UserDocumentViewModel => {
    const viewerIds = response.viewers?.map(v => v.id) || []
    const departmentAccess = response.departmentAccess || []
    const roleAccess = response.roleAccess || []

    const userHasDepartmentAccess = departmentAccess
        .some(department => userDepartments.some(userDepartment => userDepartment.id === department.id))
    const userHasRoleAccess = roleAccess
        .some(role => userRoles.some(userRole => userRole.id === role.id))

    const downloadUrl = response.content
        ? `data:${response.mimeType};base64,${response.content}`
        : ''

    return {
        id: response.id,
        isLoketFile: false,
        isDataUrl: true,
        displayName: response.displayName,
        userCanView: userHasDepartmentAccess || userHasRoleAccess || viewerIds.includes(userId),
        createdAt: new Date(response.createdAt),
        documentType: response.documentType
            ? {
                id: response.documentType.id,
                name: response.documentType.name,
                slug: response.documentType.slug,
            }
            : undefined,
        fileName: response.fileName,
        downloadUrl,
        viewers: viewerIds,
    }
}
